import { useState } from "react";

import { Flex } from "@similarweb/ui-components/foundations";

import {
    INDUSTRIES_CODE_NAMES,
    type IndustryCodeDto,
    industryCodeDtoMap,
} from "app/pages/sales-intelligence/domain/industry/types/codes";
import InclusionGroup from "app/pages/sales-intelligence/modules/advanced-search-filtering/components/atoms/InclusionGroup";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import type { InclusionOption } from "app/pages/sales-intelligence/types/commonMaps";

import type IndustryCodesFilterModel from "../../../models/IndustryCodesFilter/IndustryCodesFilterModel";
import { IndustryCodesFilterValue } from "../../../models/IndustryCodesFilter/IndustryCodesFilterValue";
import industryCodesFilterTrackingService from "../../../services/tracking/industry-codes-filter/trackingService";
import { FilterContentContainer } from "../styles";

import { AutocompleteCodes } from "./AutocompleteCodes/AutocompleteCodes";
import { CodesDropdown } from "./CodesDropdown/CodesDropdown";
import IndustryCodesBulkAdd from "./IndustryCodesBulkAdd/IndustryCodesBulkAdd";
import { StyledDropdownsFlexContainer } from "./styles";

const IndustryCodesFilter = ({ value, filter, onFilterUpdate }: IFilterViewBaseProps<IndustryCodesFilterModel>) => {
    const filterValue = new IndustryCodesFilterValue(value);
    const { codeOptions, options } = filter;
    const { naicsCode: naicsCodes, sicCodes } = options;

    const codesFullList = {
        [industryCodeDtoMap.naics]: naicsCodes,
        [industryCodeDtoMap.sic]: sicCodes,
    };

    const [inclusionType, setInclusionType] = useState<InclusionOption>(filterValue.inclusion);
    const [codeType, setCodeType] = useState<IndustryCodeDto>(industryCodeDtoMap.naics);

    const handleInclusionChange = (inclusion: InclusionOption) => {
        setInclusionType(inclusion);

        if (filterValue.isEmpty()) {
            return;
        }

        onFilterUpdate(filterValue.with({ inclusion }));
    };

    const handleCodeUpdate = (codes: readonly string[], isSelected: boolean) => {
        onFilterUpdate(
            filterValue.with({
                inclusion: inclusionType,
                [codeType]: codes,
            }),
        );

        industryCodesFilterTrackingService.trackingIndustryCodesChange(
            INDUSTRIES_CODE_NAMES[codeType],
            isSelected ? "unselect" : "select",
        );
    };

    return (
        <FilterContentContainer>
            <InclusionGroup
                value={inclusionType}
                onChange={handleInclusionChange}
            />

            <Flex
                flexDirection="column"
                gap="16px"
            >
                <StyledDropdownsFlexContainer>
                    <CodesDropdown
                        value={codeType}
                        onUpdate={setCodeType}
                        codeOptions={codeOptions}
                    />
                    <AutocompleteCodes
                        value={codeType === industryCodeDtoMap.naics ? filterValue.naicsCodes : filterValue.sicCodes}
                        codes={codesFullList[codeType]}
                        onUpdate={handleCodeUpdate}
                    />
                </StyledDropdownsFlexContainer>

                <IndustryCodesBulkAdd
                    codeType={codeType}
                    value={filterValue}
                    sicCodes={sicCodes}
                    naicsCodes={naicsCodes}
                    inclusion={inclusionType}
                    onUpdate={onFilterUpdate}
                />
            </Flex>
        </FilterContentContainer>
    );
};

export default IndustryCodesFilter;
